import { XdrSearchResultsPanelView } from '../view/XdrSearchResultsPanelView';
import { XdrSearchResultsPanel } from '../view/template';
import { XdrSearchResultsPanelRepository } from '../repository/XdrSearchResultsPanelRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { XdrSearchResultsPanelData } from '../types';
import { XdrSearchResultsPanelWebComponent } from '..';
import { XdrSearchEventDetailsPanelWebComponent } from '../../xdrSearchEventDetailsPanel';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { GridEvents } from '../../../grid/types/common';
import { htmlDecode } from '../../../utils/escape.utils';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';
import { GroupType } from '../../../ui/gzEnumList/types';
import { SCHEMA_FIELDS_ITEMS } from '../../xdrSearch/types';
import { ResizeDirection } from '../../../types/common/types';
import { SideMenuWidthValues } from '../../../ui/gzSideMenu/types';

export class XdrSearchResultsPanelController extends BaseWebComponentController {
  public component: XdrSearchResultsPanelWebComponent;
  public dataRepository: XdrSearchResultsPanelRepository;
  public view: XdrSearchResultsPanelView;
  public viewConstructor: typeof XdrSearchResultsPanelView;
  public dataRepositoryConstructor: typeof XdrSearchResultsPanelRepository;
  public searchableFields: Array<string> = [];
  public notSearchableFields: Array<string> = ['alert.incident_number'];

  constructor(component: XdrSearchResultsPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: XdrSearchResultsPanelView,
      viewTemplate: XdrSearchResultsPanel,
      dataRepositoryConstructor: XdrSearchResultsPanelRepository,
    });

    SCHEMA_FIELDS_ITEMS.forEach(item => {
      if (this.notSearchableFields.includes(item.name)) {
        return;
      }
      this.searchableFields.push(item.name);
    });
  }

  private static prepareRawEvent(rawEvent: any): object {
    const parsedEvent = JSON.parse(htmlDecode(rawEvent));
    // hide details from details panel that can't  be hidden from backend since they are used in the grid
    if (parsedEvent.protectedEntity) {
      delete parsedEvent.protectedEntity;
    }
    if (parsedEvent.incidentsDetails) {
      delete parsedEvent.incidentsDetails;
    }
    if (parsedEvent.event_time) {
      delete parsedEvent.event_time;
    }
    if (parsedEvent.event_id) {
      delete parsedEvent.event_id;
    }

    return parsedEvent;
  }

  computeEventListeners(): IListenerConfig[] {
    this.view.dom.$gridView.onDataLoadingFinishedWithSuccess = this.onDataLoadingFinishedWithSuccess.bind(this);
    return [
      {
        element: this.view.dom.$gridView,
        event: GridEvents.ROW_CLICKED,
        callback: this.onRowClick,
      },
    ];
  }

  onRowClick(evt: WebComponentEvent): void {
    if (evt.detail.rowDetails) {
      const panel = this.buildEventDetailsPanel(evt.detail.rowDetails);
      this.showEventDetailsPanel(panel);
    }
  }

  async getDataForView(): Promise<XdrSearchResultsPanelData> {
    const res = await this.dataRepository.getDataForView();
    const defaultViewProps = { ...res.defaultViewProps, gridSettingsColumnsGroupType: GroupType.ACCORDION };
    const displayedColumns = res.views[0].displayedColumns;
    const selectedTargets = res.views[0].selectedTargets;
    const filters = res.views[0].filters;
    return {
      targets: res.targets,
      isPartner: res.isPartner,
      allColumns: res.allColumns,
      views: res.views.map(view => {
        return {
          id: view.id,
          name: view.name,
          type: view.type,
          selectedTargets: selectedTargets,
          displayedColumns: displayedColumns,
          filters: filters,
          compactView: view.compactView,
          pageSize: view.pageSize,
        };
      }),
      defaultViewProps: defaultViewProps,
    };
  }

  private async onDataLoadingFinishedWithSuccess() {
    if (this.view.dom.$gridView.extraParams.companyId) {
      this.view.removeElementClass(this.view.dom.$gridView, 'hidden');
    }
  }

  private showEventDetailsPanel(panel: XdrSearchEventDetailsPanelWebComponent) {
    const xdrSearch =
      document.body.querySelector('gz-xdr-search-app') || document.body.querySelector('gz-xdr-search-tab-panel');
    let xdrSearchShadow;
    let sideMenu;

    if (xdrSearch) {
      xdrSearchShadow = xdrSearch.shadowRoot;
      sideMenu = xdrSearchShadow.querySelector('gz-side-menu');
    } else {
      sideMenu = new GzSideMenuWebComponent();
      xdrSearchShadow = this.view.dom.$wrapper;
      xdrSearchShadow.appendChild(sideMenu);
    }

    sideMenu.setMenuContent(panel);
    sideMenu.openSideMenu();
    sideMenu.resizable = ResizeDirection.LEFT;
    sideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
  }

  private buildEventDetailsPanel(details: any) {
    const translations = this.view.allTranslations.detailsPanel;
    const timezone = this.dependencies.getTimezone();
    const locale = this.dependencies.getUserLanguage();

    const panel = new XdrSearchEventDetailsPanelWebComponent();
    panel.panelConfig = {
      severityTitle: translations.severity,
      severityHigh: translations.severityLevel.high,
      severityMedium: translations.severityLevel.medium,
      severityLow: translations.severityLevel.low,
      detectedByTitle: translations.detectedBy,
      detectedOnTitle: translations.detectedOn,
      detailsTitle: translations.details,
      jsonTitle: translations.json,
      tableTitle: translations.table,
      typeTitle: translations.type,
      eventName: details.otherEventName,
      severity: details.alertSeverityScore ? details.alertSeverityScore : 0,
      detectedBy: details.otherSensorName,
      detectedOn: getFormattedDateTime(TimestampFormat.EVENTS, details.eventTime, timezone, locale),
      type: details.otherEventType,
      rawEvent: XdrSearchResultsPanelController.prepareRawEvent(details.rawEvent),
      searchableFields: this.searchableFields,
      showMore: translations.showMore,
      showLess: translations.showLess,
      emptyJsonText: translations.emptyJsonText,
      copyBtnString: translations.copyBtnString,
      confirmationCopy: translations.confirmationCopy,
    };

    return panel;
  }
}
